import { Injectable } from '@angular/core';
import * as moment from 'moment';
import { CacheData } from './entities/cacheData.class';
import { CacheVisit } from './entities/cacheVisit.class';
import { CacheTime } from './entities/cacheTime.class';
import { CacheConfiguration } from './entities/cacheConfig.class';
import { MainMenuPageEntity } from './entities/mainMenuPage.class';
import { RequestService } from './requestService.service';
import { Worker } from './shared';
import { CacheUserStatus } from './entities/cacheUserStatus.class';
import { CacheAssignedPatient } from './entities/cacheAssignedPatient.class';
import { DateFormat, DateTimeFormat, MomentDefaultFormat } from './entities/constantsExport.class';
import { CacheCarePlanItem } from './entities/cacheCarePlanItem.class';
import { CacheConfigurableListItem } from './entities/cacheConfigurableListItem.class';
import { StorageService } from './storageservice';
import { CacheLocation } from './entities/cacheLocation.class';
import { Platform } from '@ionic/angular';
import { DeviceVersion } from './entities/deviceVersion.class';
import { MemberInfo } from './entities/memberInfo.class';
import { CacheTranslation } from './entities/cacheTranslation.class';
import { PointGroup } from 'angular2-signaturepad/angular2-signaturepad';
import { RollbarErrorHandler } from './rollbar.service';
import { BusinessConfigurationMessages } from './enumerations.class';
import { MomentFormats } from 'src/shared/entities/momentFormats.class';
import { Capacitor } from '@capacitor/core';

@Injectable()
export class OfflineManager {
    public cacheData: CacheData = new CacheData(
        [],
        [],
        [],
        <Worker>{},
        [],
        [],
        [],
        [],
        [],
        <DeviceVersion>{},
        <MemberInfo>{},
        []
    );
    public sessionVisitList: CacheVisit[] = [];
    // public sessionUser: Worker;
    public sessionCurrentVisit: CacheVisit;
    public readonly cacheVisitKey = "VisitData";
    public readonly cacheConfigKey = "ConfigData";
    public readonly cacheMenuKey = "MainMenuData"
    public readonly cacheUserKey = "UserData";
    public readonly AdditionalCarePlanItemsKey = "AdditionalCarePlanItems";
    public readonly cacheErrorKey = "ErrorData";
    public readonly cacheUserStatusKey = "UserStatus";
    public readonly cacheUserLog = "UserLog";
    public readonly cacheAssignedPatientsKey = "AssignedPatients";
    public readonly cacheConfigurableListItemKey = "ConfigurableList";
    public readonly cacheLocationsKey = "Locations";
    public readonly cacheMemberInfoKey = "MemberInfo";
    public readonly cacheTranslationsKey = "Translations";
    public readonly cacheTimesKey = "CacheTimes";
    public readonly workerCoordinates = "WorkerCoordinates";

    public signatureData: any;
    public signatureImage: PointGroup[] = [];
    public signatureCleared: boolean = false;
    public uptimeTolerance: number = 0;
    public debugCacheTimes: string = "";
    constructor(
        private requestService: RequestService,
        private storage: StorageService,
        private platform: Platform,
        private rollbarErrorHandler: RollbarErrorHandler
    ) { }

    public patientSignatureAccepted = false;
    public workerSignatureAccepted = false;
    public isPatientSignature = false;

    addVisitToCache(visit: CacheVisit) {
        return new Promise<any>(resolve => {
            var visits: CacheVisit[] = [];
            this.storage.get(this.cacheVisitKey).then(data => {
                visits = data ? data : [];
                visits.push(visit);
                this.storage.set(this.cacheVisitKey, visits).then(() => {
                    resolve([]);
                });
            });
        });
    }

    getConfigurationValue(message: string) {
        var config: CacheConfiguration = this.cacheData.Configs.find(c => c.Message == message);

        if (!config || !config.Active) {
            return undefined;
        }

        return config.Value;
    }

    getDataForCache() {
        return new Promise<any>(resolve => {
            let start = moment().format(DateFormat);
            let end = moment().add(1, 'days').format(DateFormat)
            if (this.requestService.workerData !== undefined) {
                this.requestService.getCacheData(start, end).then(cacheData => {
                    this.cacheData = cacheData;
                    this.cacheData.Visits = cacheData.Visits.map(x => new CacheVisit(x)
                    );
                    this.cacheData.Configs = cacheData.Configs.map(x => new CacheConfiguration(
                        x.ConfigurationTypeID,
                        x.Active,
                        x.Value,
                        x.Message,
                        x.SettingType
                    ));
                    this.cacheData.MainMenuPages = cacheData.MainMenuPages.map(x => new MainMenuPageEntity(
                        x.TabName,
                        x.Value
                    ));
                    this.cacheData.AssignedPatients = cacheData.AssignedPatients.map(x => new CacheAssignedPatient(
                        x.PatientId,
                        x.PatientFirstName,
                        x.PatientLastName,
                        x.ServiceTypeID,
                        x.ServiceTypeName,
                        x.VisitDuration,
                        x.PatientTime,
                        x.PatientZone,
                        x.SistemPatientID2,
                        x.UnitCostRate
                    ));
                    this.cacheData.AdditionalCarePlanItems = cacheData.AdditionalCarePlanItems.map(x => new CacheCarePlanItem(x));

                    this.cacheData.ConfigurableList = cacheData.ConfigurableList.map(x => new CacheConfigurableListItem(x));

                    this.cacheData.Locations = [];

                    this.cacheData.Translations = cacheData.Translations.map(x => new CacheTranslation(x));

                    var setVisit = this.storage.set("VisitData", this.cacheData.Visits);
                    var setConfigs = this.storage.set("ConfigData", this.cacheData.Configs);
                    var setMainMenu = this.storage.set("MainMenuData", this.cacheData.MainMenuPages);
                    var setAssignedPatients = this.storage.set("AssignedPatients", this.cacheData.AssignedPatients);
                    var additionalCarePlanItems = this.storage.set("AdditionalCarePlanItems", this.cacheData.AdditionalCarePlanItems);
                    var configurableList = this.storage.set("ConfigurableList", this.cacheData.ConfigurableList);
                    var locations = this.storage.set("Locations", this.cacheData.Locations);
                    var memberInfo = this.storage.set("MemberInfo", cacheData.MemberInfo);
                    var translations = this.storage.set(this.cacheTranslationsKey, cacheData.Translations);

                    Promise.all([setVisit, setConfigs, setMainMenu, setAssignedPatients, additionalCarePlanItems, configurableList, locations, memberInfo, translations]).then(data => {
                        resolve([]);
                    });
                });
            } else {
                resolve([]);
            }
        });
    }

    updateCacheVisit() {
        this.sessionCurrentVisit.NeedsSync = true;
        this.sessionCurrentVisit.ModifiedDate = new Date();

        return new Promise<any>(resolve => {
            this.storage.get(this.cacheVisitKey).then((visits: CacheVisit[]) => {
                var visitList: CacheVisit[] = [];
                visitList = visits ? visits.map(x => new CacheVisit(x)) : [];

                var visitIndex = visitList.findIndex(v => (this.sessionCurrentVisit.ServiceOrderId == 0
                    && v.ServiceOrderIdString == this.sessionCurrentVisit.ServiceOrderIdString)
                    ||
                    (this.sessionCurrentVisit.ServiceOrderId > 0
                        && v.ServiceOrderId == this.sessionCurrentVisit.ServiceOrderId));

                if (visitIndex >= 0) {
                    visitList.splice(visitIndex, 1, this.sessionCurrentVisit);
                    this.storage.set(this.cacheVisitKey, visitList).then(() => {
                        resolve([]);
                    });
                }
                else {
                    resolve([]);
                }
            })
        })
    }

    updateCacheVisit_AfterSync(visit: CacheVisit) {
        var visit = new CacheVisit(visit);

        return new Promise<any>(resolve => {
            this.storage.get(this.cacheVisitKey).then(visits => {
                var visitList: CacheVisit[] = [];
                visitList = visits;

                var visitIndex = visitList.findIndex(v => (v.ServiceOrderId == 0 && v.ServiceOrderIdString == visit.ServiceOrderIdString)
                    || (v.ServiceOrderId > 0 && v.ServiceOrderId == visit.ServiceOrderId));
                if (visitIndex >= 0) {
                    visit.CarePlanItems = visitList[visitIndex].CarePlanItems;
                    visit.PatientAddressList = visitList[visitIndex].PatientAddressList;
                    visit.SignatureData = visitList[visitIndex].SignatureData;
                    visitList.splice(visitIndex, 1, visit);
                    this.storage.set(this.cacheVisitKey, visitList).then(data => {
                        resolve([]);
                    });
                }
                else {
                    resolve([]);
                }
            })
        })
    }

    mapObjectsFromCache() {
        return new Promise<any>(resolve => {
            var configs = this.mapConfigs();
            var visits = this.mapVisits();
            var menuPages = this.mapMenuPages();
            var worker = this.mapWorker();
            var assignedPatients = this.mapAssignedPatiens();
            Promise.all([configs, visits, menuPages, worker, assignedPatients]).then(data => {
                resolve([]);
            })
        })

    }
    mapObjectsFromCache2() {
        return new Promise<any>(resolve => {
            var menuPages = this.mapMenuPages();

            Promise.all([menuPages]).then(data => {
                resolve([]);
            })
        })

    }
    mapConfigs() {
        return new Promise<any>(resolve => {
            this.storage.get(this.cacheConfigKey).then(data => {
                this.cacheData.Configs = data;
                resolve([]);
            })
        })
    }

    mapVisits() {
        return new Promise<any>(resolve => {
            this.storage.get(this.cacheVisitKey).then(data => {
                this.cacheData.Visits = data;
                resolve([]);
            })
        })
    }

    mapMenuPages() {
        return new Promise<any>(resolve => {
            this.storage.get(this.cacheMenuKey).then(data => {
                this.cacheData.MainMenuPages = data;
                resolve([]);
            })
        })
    }

    mapAssignedPatiens() {
        return new Promise<any>(resolve => {
            this.storage.get(this.cacheAssignedPatientsKey).then(data => {
                this.cacheData.AssignedPatients = data;
                resolve([]);
            })
        })
    }

    mapWorker() {
        return new Promise<any>(resolve => {
            this.storage.get(this.cacheUserKey).then(data => {
                this.cacheData.Worker = data;
                resolve([]);
            })
        })
    }

    getDevicePlatform(): string {
        const platform = Capacitor.getPlatform();

        switch (platform) {
            case 'ios':
                return 'IOS';
            case 'android':
                return 'Android';
            case 'web':
                return 'web';
            case 'electron':
                return 'desktop';
            case 'mobileweb':
                return 'mobileWeb';
            default:
                return 'Unknown';
        }
    }

    getCacheData() {

        let deviceVersion = new DeviceVersion(
            this.getDevicePlatform(),
            this.requestService.getAppVersion()
        );

        let cacheData: CacheData = new CacheData(
            [],
            [],
            [],
            <Worker>{},
            [],
            [],
            [],
            [],
            [],
            <DeviceVersion>{},
            <MemberInfo>{},
            []
        );

        let cacheVisits = this.storage.get(this.cacheVisitKey);
        let cacheConfig = this.storage.get(this.cacheConfigKey);
        let cacheMenu = this.storage.get(this.cacheMenuKey);
        let cacheError = this.storage.get(this.cacheErrorKey);
        let cacheUser = this.storage.get(this.cacheUserKey);
        let cacheAssignedPatients = this.storage.get(this.cacheAssignedPatientsKey);
        let cacheConfigurableList = this.storage.get(this.cacheConfigurableListItemKey);
        let cacheLocations = this.storage.get(this.cacheLocationsKey);
        let devVersion = new Promise<any>(resolve => resolve(deviceVersion))
        let memberInfo = this.storage.get(this.cacheMemberInfoKey);
        let cacheTimes = this.storage.get(this.cacheTimesKey);

        return new Promise<CacheData>(resolve => {
            Promise.all([cacheVisits, cacheConfig, cacheMenu, cacheUser, cacheError, cacheAssignedPatients, cacheConfigurableList, cacheLocations, devVersion, memberInfo, cacheTimes]).then(data => {
                cacheData.Visits = <CacheVisit[]>data[0];
                if (this.uptimeTolerance > 0 && (Capacitor.getPlatform() === 'android' || Capacitor.getPlatform() === 'ios')) {
                    var cacheTimesArray = <CacheTime[]>data[10];
                    if (cacheTimesArray != null) {
                        cacheData.Visits = this.checkOfflineTimes(cacheData.Visits, cacheTimesArray);
                    }
                }
                cacheData.Configs = <CacheConfiguration[]>data[1];
                cacheData.MainMenuPages = <MainMenuPageEntity[]>data[2];
                cacheData.Worker = <Worker>data[3];
                cacheData.Errors = <string[]>data[4];
                cacheData.AssignedPatients = <CacheAssignedPatient[]>data[5];
                cacheData.ConfigurableList = <CacheConfigurableListItem[]>data[6];
                cacheData.Locations = <CacheLocation[]>data[7];
                cacheData.DeviceVersion = <DeviceVersion>data[8];
                cacheData.MemberInfo = <MemberInfo>data[9];
                resolve(cacheData);
            })
        })
    }

    checkOfflineTimes(visits: CacheVisit[], cacheTimesArray: CacheTime[]) {
        let visitArrived = false;
        for (let i = 0; i < cacheTimesArray.length; i++) {
            if (cacheTimesArray[i].synced == false && cacheTimesArray[i].serviceOrderId != -1 && cacheTimesArray[i].serviceOrderId != 0) {
                let index = visits.findIndex(x => x.ServiceOrderId == cacheTimesArray[i].serviceOrderId && x.ServiceOrderId != 0);
                if (index != -1) {
                    let timeZone = cacheTimesArray[i].timezone;
                    let cacheTimeStr = moment(cacheTimesArray[i].time, MomentFormats.momentFormats).format(DateTimeFormat);
                    let timeCache = moment(cacheTimeStr, DateTimeFormat).add(timeZone);
                    visits[index].RestartTimeArrival = 0;
                    visits[index].RestartTimeDeparture = 0;
                    visitArrived = true;
                    if (cacheTimesArray[i].isArrival) {
                        let timeDifference = moment(visits[index].ArrivalTime, DateTimeFormat).diff(timeCache, 'minutes');
                        visits[index].ArrivalTime = (Math.abs(timeDifference) > this.uptimeTolerance) ? timeCache.format(DateTimeFormat) : visits[index].ArrivalTime;
                        visits[index].RestartTimeArrival = (0 < Math.abs(cacheTimesArray[i].restart) && Math.abs(cacheTimesArray[i].restart) < 60000) ? 1 : Math.floor(cacheTimesArray[i].restart / 60000);
                        visits[index].NegativeTimeViolation = cacheTimesArray[i].negative;
                    } else {
                        let timeDifference = moment(visits[index].DepartureTime, DateTimeFormat).diff(timeCache, 'minutes');
                        visits[index].DepartureTime = (Math.abs(timeDifference) > this.uptimeTolerance) ? timeCache.format(DateTimeFormat) : visits[index].DepartureTime;
                        let index2 = cacheTimesArray.findIndex(x => x.serviceOrderId == cacheTimesArray[i].serviceOrderId && x.isArrival == true);
                        if (index2 != -1) {
                            visits[index].RestartTimeArrival = (0 < Math.abs(cacheTimesArray[index2].restart) && Math.abs(cacheTimesArray[index2].restart) < 60000) ? 1 : Math.floor(cacheTimesArray[index2].restart / 60000);
                            visits[index].NegativeTimeViolation = cacheTimesArray[index2].negative;
                        }
                        visits[index].RestartTimeDeparture = (0 < Math.abs(cacheTimesArray[i].restart) && Math.abs(cacheTimesArray[i].restart) < 60000) ? 1 : Math.floor(cacheTimesArray[i].restart / 60000);
                        visits[index].NegativeTimeViolation = visits[index].NegativeTimeViolation || cacheTimesArray[i].negative;
                    }
                    visits[index].RestartSubmit = true;
                }
            }
        }
        for (let i = 0; i < visits.length; i++) {
            if (visits[i].ServiceOrderId == 0) {
                let indexArrival = cacheTimesArray.findIndex(x => x.serviceOrderId == 0 && x.isArrival == true && x.patientServiceOrderId == visits[i].PatientServiceOrderId);
                let indexDeparture = cacheTimesArray.findIndex(x => x.serviceOrderId == 0 && x.isArrival == false && x.patientServiceOrderId == visits[i].PatientServiceOrderId);
                if (indexArrival != -1) {
                    visitArrived = true;
                    let timeZone = cacheTimesArray[indexArrival].timezone;
                    let cacheTimeStr = moment(cacheTimesArray[indexArrival].time, MomentFormats.momentFormats).format(DateTimeFormat);
                    let timeCache = moment(cacheTimeStr, DateTimeFormat).add(timeZone);
                    let timeDifference = moment(visits[i].ArrivalTime, DateTimeFormat).diff(timeCache, 'minutes');

                    visits[i].ArrivalTime = (Math.abs(timeDifference) > this.uptimeTolerance) ? timeCache.format(DateTimeFormat) : visits[i].ArrivalTime;
                    visits[i].RestartTimeArrival = Math.floor(cacheTimesArray[indexArrival].restart / 60000);
                    visits[i].NegativeTimeViolation = visits[i].NegativeTimeViolation || cacheTimesArray[indexArrival].negative;
                }
                if (indexDeparture != -1) {
                    visitArrived = true;
                    let timeZone = cacheTimesArray[indexDeparture].timezone;
                    let cacheTimeStr = moment(cacheTimesArray[indexDeparture].time, MomentFormats.momentFormats).format(DateTimeFormat);
                    let timeCache = moment(cacheTimeStr, DateTimeFormat).add(timeZone);
                    let timeDifference = moment(visits[i].DepartureTime, DateTimeFormat).diff(timeCache, 'minutes');

                    visits[i].DepartureTime = (Math.abs(timeDifference) > this.uptimeTolerance) ? timeCache.format(DateTimeFormat) : visits[i].DepartureTime;
                    visits[i].RestartTimeDeparture = Math.floor(cacheTimesArray[indexDeparture].restart / 60000);
                    visits[i].NegativeTimeViolation = visits[i].NegativeTimeViolation || cacheTimesArray[indexDeparture].negative;
                }
                visits[i].RestartSubmit = true;
            }
        }
        this.debugCacheTimes = "Cache Time Points: \n";
        for (let i = 0; i < cacheTimesArray.length; i++) {
            this.debugCacheTimes += i + " " + cacheTimesArray[i].serviceOrderId + " " + cacheTimesArray[i].isArrival + " "
                + moment(cacheTimesArray[i].time, MomentFormats.momentFormats).format(DateTimeFormat) + " " + cacheTimesArray[i].timezone + " " + cacheTimesArray[i].uptime + " "
                + cacheTimesArray[i].synced + " " + cacheTimesArray[i].restart + " " + cacheTimesArray[i].negative + " pID: " + cacheTimesArray[i].patientServiceOrderId + " " + cacheTimesArray[i].time + " \n";
        }
        this.debugCacheTimes += "Visits: \n";
        for (let i = 0; i < visits.length; i++) {
            this.debugCacheTimes += i + " " + visits[i].ServiceOrderId + " " + visits[i].ArrivalTime + " " + visits[i].DepartureTime + " " + visits[i].PatientServiceOrderId + " \n";
        }

        if (visits.length > 0) {
            this.rollbarErrorHandler.offlineLog("Offline cache sync: \n" + this.debugCacheTimes);
        }
        return visits;
    }

    setCacheUserStatus(obj: CacheUserStatus) {
        return new Promise<any>(resolve => {
            this.storage.set(this.cacheUserStatusKey, obj).then(() => {
                resolve([]);
            })
        })
    }

    setPayableHoursInfo(obj: MemberInfo) {
        return new Promise<any>(resolve => {
            this.storage.set(this.cacheMemberInfoKey, obj).then(() => {
                resolve([]);
            })
        })
    }
    clearCacheData() {
        return Promise.all(
            [
                this.storage.remove(this.cacheUserStatusKey),
                //this.storage.remove(this.cacheAssignedPatientsKey),
                //this.storage.remove(this.cacheConfigKey),
                this.storage.remove(this.cacheConfigurableListItemKey),
                this.storage.remove(this.cacheErrorKey),
                //this.storage.remove(this.cacheMenuKey),
                //localStorage.getItem(this.requestService.visitSyncFailedKey) == "true" ? "" : this.storage.remove(this.cacheVisitKey),
                //this.storage.remove(this.cacheUserKey),
                this.storage.remove(this.AdditionalCarePlanItemsKey),
                this.storage.remove(this.cacheLocationsKey),
                this.storage.remove(this.cacheMemberInfoKey),
                this.storage.remove(this.cacheTranslationsKey),
                this.storage.remove(this.cacheTimesKey)
            ]
        );
    }

    clearLocationCacheData() {
        return new Promise<any>(resolve => {
            this.storage.set(this.cacheLocationsKey, []);
        });
    }

    setLogin(username, password, device) {
        const data = {
            username: btoa(username),
            password: btoa(password),
            device: btoa(device)
        };
        this.storage.set(this.cacheUserLog, data);
    }
    setBiometricLogin(username, device) {
        this.storage.get(this.cacheUserLog).then(data => {
            if (!data) {
                data = {};
            }
            data.username = btoa(username);
            data.device = btoa(device);
            this.storage.set(this.cacheUserLog, data);
        });
        return;
    }

    logLocationInCache(location) {
        return new Promise<any>(resolve => {
            this.storage.get(this.cacheLocationsKey).then((data: CacheLocation[]) => {
                var locations: CacheLocation[] = [];
                if (data == null) {
                    data = [] as CacheLocation[];
                }

                const maxLocations = 10;
                const length = data.length;

                let startIndex = 0;

                if (length >= maxLocations) {
                    startIndex = length - maxLocations + 1;
                }

                locations = data.slice(startIndex, length)
                    .map(l => new CacheLocation(l));

                var locationToAdd =
                    {
                        Latitude: -1,
                        Longitude: -1,
                        TimeStamp: moment().valueOf(),
                        ServiceOrderId: this.sessionCurrentVisit == null ? -1 : this.sessionCurrentVisit.ServiceOrderId
                    } as CacheLocation;
                if (location && location.coords && location.timestamp) {
                    locationToAdd.Latitude = location.coords.latitude;
                    locationToAdd.Longitude = location.coords.longitude;
                }
                locations.push(locationToAdd);
                this.storage.set(this.cacheLocationsKey, locations).then(() => {
                    resolve([]);
                });
            });
        });
    }
}